import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpenText, CalendarDays, ClipboardCheck, FileSearch, Library } from "lucide-react";
import { Seo, breadcrumbSchema } from "@/components/site/Seo";
import { PageHero, LifestyleImage } from "@/components/site/PageHero";
import { Skeleton } from "@/components/ui/skeleton";
import { Disclaimer } from "@/components/site/Disclaimer";
import { SignupForm } from "@/components/site/SignupForm";
import { api } from "@/lib/api";
import { IMAGES } from "@/config/site";

const crumbs = [
  { name: "Home", path: "/" },
  { name: "Wellness Guide", path: "/wellness-guide" },
];

const UPCOMING = [
  "Understanding Sea Moss Gel",
  "Questions to Ask Before Buying Wellness Supplements",
  "How to Read Product Ingredients and Labels",
  "Understanding the Difference Between Food, Beverages and Dietary Supplements",
];

export const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" }) : "");

export default function WellnessGuide() {
  const [articles, setArticles] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setArticles(r.data || []))
      .catch(() => {
        setError(true);
        setArticles([]);
      });
  }, []);
  const hasArticles = articles && articles.length > 0;

  return (
    <>
      <Seo
        title="Uhlumo Wellness Guide | Product Information & Resources"
        description="The Uhlumo Wellness Guide helps customers understand our natural wellness products and make informed decisions. Articles are reviewed before publication."
        path="/wellness-guide"
        noindex={!hasArticles}
        jsonLd={[breadcrumbSchema(crumbs)]}
      />
      <PageHero
        crumbs={crumbs}
        eyebrow={
          <>
            <BookOpenText className="h-3.5 w-3.5" aria-hidden="true" /> Resources
          </>
        }
        title="Uhlumo Wellness Guide"
        intro="Helping you understand our products and make informed decisions. Every health-related article uses reliable sources and is reviewed before it is published."
        aside={<LifestyleImage id={IMAGES.botanical.id} alt={IMAGES.botanical.alt} priority className="aspect-[4/3]" />}
      />

      <section className="section" aria-labelledby="articles-heading" data-testid="wellness-guide-articles">
        <div className="container-site">
          <h2 id="articles-heading" className="h-section">
            {hasArticles ? "Latest articles" : "Articles in editorial review"}
          </h2>
          {articles === null ? (
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {[0, 1].map((i) => (
                <Skeleton key={i} className="h-40 rounded-2xl" />
              ))}
            </div>
          ) : hasArticles ? (
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {articles.map((a) => (
                <Link key={a.slug} to={`/wellness-guide/${a.slug}`} className="card-soft card-hover block p-7" data-testid={`article-card-${a.slug}`}>
                  <p className="flex items-center gap-2 text-xs text-muted-foreground">
                    <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                    {formatDate(a.published_at)} · {a.author}
                  </p>
                  <h3 className="mt-3 text-2xl font-semibold">{a.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{a.excerpt}</p>
                  <span className="link-accent mt-4 inline-block text-sm">Read article →</span>
                </Link>
              ))}
            </div>
          ) : (
            <>
              <p className="lead mt-3 max-w-2xl">
                {error ? "We could not load articles right now. " : ""}Our first articles are being researched and reviewed. They will appear here once approved.
              </p>
              <ul className="mt-8 grid gap-4 md:grid-cols-2" data-testid="wellness-upcoming-topics">
                {UPCOMING.map((t) => (
                  <li key={t} className="card-soft flex items-start justify-between gap-4 p-6">
                    <span className="font-display text-lg font-semibold leading-snug">{t}</span>
                    <span className="badge-info shrink-0">Coming soon</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>

      <section className="section surface-sage" aria-labelledby="standards-heading">
        <div className="container-site">
          <h2 id="standards-heading" className="h-section">
            Our editorial standards
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {[
              { icon: Library, t: "Reliable sources", b: "Articles reference reliable, published sources. We never invent research or citations." },
              { icon: ClipboardCheck, t: "Reviewed before publishing", b: "Health-related content is reviewed and dated, with a “last reviewed” date on every article." },
              { icon: FileSearch, t: "Education, not advice", b: "Our guide is general information only and is not a substitute for advice from a healthcare professional." },
            ].map(({ icon: Icon, t, b }) => (
              <div key={t} className="card-soft p-6">
                <Icon className="h-6 w-6 text-[color:var(--u-blue)]" aria-hidden="true" />
                <h3 className="mt-4 text-lg font-semibold">{t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="guide-signup-heading">
        <div className="container-site grid gap-10 lg:grid-cols-2">
          <div>
            <h2 id="guide-signup-heading" className="h-section">
              Be the first to read new articles
            </h2>
            <p className="lead mt-3">Join the Uhlumo Organics Community for product news and new Wellness Guide articles.</p>
            <div className="mt-6">
              <Disclaimer compact />
            </div>
          </div>
          <div className="card-soft p-6 sm:p-8">
            <SignupForm idPrefix="guide-signup" />
          </div>
        </div>
      </section>
    </>
  );
}
